// CRMReports — formal, exportable sales report for the CRM tab.
//
// Three sections, each switchable in the settings panel:
//   * YTD performance   — closed-won revenue, win rate, new ARR, monthly
//                         closed revenue for the fiscal year to date.
//   * Forecast          — open pipeline by expected close month, split
//                         into Commit / Upside / Pipeline by probability,
//                         with weighted totals and a projected FY finish.
//   * Funnel            — stage-by-stage snapshot, stage-to-stage
//                         conversion, where deals are lost.
// plus an optional appendix listing every open deal.
//
// Settings (fiscal year start, forecast horizon, probability thresholds,
// owner filter, commentary) persist per org in localStorage under
// `nuggets_crm_report_<orgId>` — they are reporting preferences, not
// business data, so they don't need a DB table.
//
// The preview on the right IS the print layout: a white, letter-width
// page rendered with the same markup the PDF exporter captures. Export
// clones that DOM off-screen, rasterises each `[data-report-block]`
// separately with html2canvas, and packs the blocks onto letter pages
// with jsPDF so a section never gets sliced mid-table unless it is
// taller than a whole page.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FileDown, RotateCcw } from 'lucide-react';

import { buildReportModel, DEFAULT_REPORT_SETTINGS, MONTHS, money, pct, fmtDate, todayIso } from './reportModel.js';

// ─── print-layout primitives (white paper) ───────────────────────
const P = {
  font:   "'Montserrat', 'Segoe UI', Arial, sans-serif",
  ink:    '#111827',
  muted:  '#6B7280',
  line:   '#E5E7EB',
  accent: '#E87722',
  navy:   '#0D1B2A',
  tile:   '#F5F6F8',
};
const Block = ({ children, style }) => (
  <div data-report-block style={{ padding: '0 48px', marginBottom: 22, ...style }}>{children}</div>
);
const H2 = ({ children, sub }) => (
  <div style={{ borderBottom: `2px solid ${P.accent}`, paddingBottom: 6, marginBottom: 14 }}>
    <div style={{ fontFamily: P.font, fontSize: 15, fontWeight: 800, color: P.navy, letterSpacing: '0.02em', textTransform: 'uppercase' }}>{children}</div>
    {sub && <div style={{ fontFamily: P.font, fontSize: 10, color: P.muted, marginTop: 2 }}>{sub}</div>}
  </div>
);
const Kpis = ({ items }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(items.length, 4)}, 1fr)`, gap: 10, marginBottom: 14 }}>
    {items.map(k => (
      <div key={k.label} style={{ background: P.tile, border: `1px solid ${P.line}`, borderRadius: 6, padding: '10px 12px' }}>
        <div style={{ fontFamily: P.font, fontSize: 8.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: P.muted, marginBottom: 4 }}>{k.label}</div>
        <div style={{ fontFamily: P.font, fontSize: 19, fontWeight: 900, color: k.color || P.navy, lineHeight: 1.1 }}>{k.value}</div>
        {k.sub && <div style={{ fontFamily: P.font, fontSize: 9, color: P.muted, marginTop: 3 }}>{k.sub}</div>}
      </div>
    ))}
  </div>
);
const Table = ({ cols, rows, foot, empty = 'No records in this period.' }) => (
  <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: P.font, fontSize: 10.5, color: P.ink }}>
    <thead>
      <tr>{cols.map(c => (
        <th key={c.key} style={{ textAlign: c.align || 'left', background: P.navy, color: '#fff', padding: '6px 8px', fontWeight: 700, fontSize: 9.5, letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{c.label}</th>
      ))}</tr>
    </thead>
    <tbody>
      {rows.length === 0 && <tr><td colSpan={cols.length} style={{ padding: '10px 8px', color: P.muted, fontStyle: 'italic' }}>{empty}</td></tr>}
      {rows.map((r, i) => (
        <tr key={r.id || i} style={{ background: i % 2 ? '#FAFAFB' : '#fff' }}>
          {cols.map(c => (
            <td key={c.key} style={{ padding: '5px 8px', borderBottom: `1px solid ${P.line}`, textAlign: c.align || 'left', whiteSpace: c.nowrap ? 'nowrap' : 'normal', color: c.color?.(r) || P.ink, fontWeight: c.bold ? 700 : 400 }}>
              {c.render ? c.render(r) : r[c.key]}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
    {foot && (
      <tfoot><tr>{cols.map(c => (
        <td key={c.key} style={{ padding: '6px 8px', borderTop: `2px solid ${P.navy}`, textAlign: c.align || 'left', fontWeight: 800, whiteSpace: 'nowrap' }}>{foot[c.key] ?? ''}</td>
      ))}</tr></tfoot>
    )}
  </table>
);
// Div-based bars: html2canvas rasterises plain boxes far more reliably
// than SVG gradients, and this report has to survive the PDF path.
const Bars = ({ items, max, height = 110, color = P.accent, fmt = money }) => (
  <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', height, borderBottom: `1px solid ${P.line}`, padding: '0 2px' }}>
    {items.map(it => (
      <div key={it.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
        {it.value > 0 && <div style={{ fontFamily: P.font, fontSize: 8, color: P.muted, marginBottom: 2, whiteSpace: 'nowrap' }}>{fmt(it.value)}</div>}
        <div style={{ width: '100%', height: `${Math.max(2, (it.value / max) * (height - 30))}px`, background: it.value > 0 ? (it.color || color) : P.line, borderRadius: '3px 3px 0 0' }} />
        <div style={{ fontFamily: P.font, fontSize: 8.5, color: P.muted, marginTop: 4, whiteSpace: 'nowrap' }}>{it.label}</div>
      </div>
    ))}
  </div>
);

// ─── the paper ────────────────────────────────────────────────────
function ReportPaper({ model, settings, orgName, logoUrl, innerRef }) {
  const { ytd, forecast: fc, funnel: fn } = model;
  const S = settings.sections || DEFAULT_REPORT_SETTINGS.sections;
  const catColor = { commit: '#16A34A', upside: '#F59E0B', pipeline: '#6B7280' };
  const catLabel = { commit: 'Commit', upside: 'Upside', pipeline: 'Pipeline' };
  const periodLine = `${model.fyLabel} · ${fmtDate(model.fyStart)} – ${fmtDate(model.asOf)}`;

  return (
    <div ref={innerRef} style={{ width: 816, background: '#fff', color: P.ink, paddingTop: 44, paddingBottom: 36, boxShadow: '0 10px 40px rgba(0,0,0,0.45)' }}>
      {/* Cover / header */}
      <Block style={{ marginBottom: 26 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 20, borderBottom: `3px solid ${P.navy}`, paddingBottom: 16 }}>
          <div>
            <div style={{ fontFamily: P.font, fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: P.accent }}>{orgName}</div>
            <div style={{ fontFamily: P.font, fontSize: 26, fontWeight: 900, color: P.navy, letterSpacing: '-0.01em', marginTop: 4, lineHeight: 1.1 }}>{settings.title || 'Sales Performance Report'}</div>
            <div style={{ fontFamily: P.font, fontSize: 11, color: P.muted, marginTop: 6 }}>{periodLine}</div>
          </div>
          {logoUrl && <img src={logoUrl} alt="" crossOrigin="anonymous" style={{ maxHeight: 54, maxWidth: 160, objectFit: 'contain' }} />}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginTop: 12, fontFamily: P.font, fontSize: 10 }}>
          {[
            ['As of',        fmtDate(model.asOf)],
            ['Fiscal year',  `${MONTHS[Number(settings.fiscalYearStartMonth) || 0]} start`],
            ['Scope',        model.ownerName ? `Owner: ${model.ownerName}` : 'All deal owners'],
            ['Prepared by',  settings.preparedBy || '—'],
          ].map(([k, v]) => (
            <div key={k}><div style={{ color: P.muted, fontSize: 8.5, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{k}</div><div style={{ fontWeight: 700, marginTop: 2 }}>{v}</div></div>
          ))}
        </div>
        {settings.commentary?.trim() && (
          <div style={{ marginTop: 14, padding: '10px 14px', borderLeft: `3px solid ${P.accent}`, background: P.tile, fontFamily: P.font, fontSize: 11, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
            {settings.commentary.trim()}
          </div>
        )}
      </Block>

      {/* Executive summary strip — always on */}
      <Block>
        <H2 sub="Headline numbers for the period, forecast horizon and funnel health.">Executive Summary</H2>
        <Kpis items={[
          { label: `Closed won ${model.fyLabel} YTD`, value: money(ytd.wonValue), sub: `${ytd.wonCount} deal${ytd.wonCount === 1 ? '' : 's'}`, color: '#16A34A' },
          { label: 'Projected FY (weighted)', value: money(fc.projection.weighted), sub: `YTD + ${money(fc.projection.weighted - ytd.wonValue)} weighted` },
          { label: 'Open pipeline', value: money(fc.projection.openTotal), sub: `${fn.openCount} open · ${money(fc.projection.openWeighted)} weighted`, color: P.accent },
          { label: 'Win rate YTD', value: pct(ytd.winRate), sub: `all-time ${pct(ytd.winRateAll)}` },
        ]} />
      </Block>

      {S.ytd && (
        <Block>
          <H2 sub={periodLine}>Year-to-Date Performance</H2>
          <Kpis items={[
            { label: 'Closed won revenue', value: money(ytd.wonValue), color: '#16A34A' },
            { label: 'Average deal size',  value: money(ytd.avgDeal), sub: Number.isFinite(ytd.avgCycle) ? `${Math.round(ytd.avgCycle)} day avg cycle` : undefined },
            { label: 'New ARR booked',     value: money(ytd.newArr), sub: `${money(ytd.totalArr)} total active ARR` },
            { label: 'Deals created',      value: String(ytd.newCount), sub: `${ytd.wonCount} won · ${ytd.lostCount} lost` },
          ]} />
          <div style={{ fontFamily: P.font, fontSize: 9.5, fontWeight: 700, color: P.muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>Closed-won revenue by month</div>
          <Bars items={ytd.months.map(m => ({ label: m.label, value: m.wonValue }))} max={ytd.maxMonth} color="#16A34A" />
          <div style={{ height: 12 }} />
          <Table
            cols={[
              { key: 'label',      label: 'Month' },
              { key: 'newCount',   label: 'Created',    align: 'right' },
              { key: 'wonCount',   label: 'Won',        align: 'right' },
              { key: 'lostCount',  label: 'Lost',       align: 'right' },
              { key: 'wonValue',   label: 'Won value',  align: 'right', render: r => money(r.wonValue) },
              { key: 'cumulative', label: 'Cumulative', align: 'right', render: r => money(r.cumulative), bold: true },
            ]}
            rows={ytd.months.map(m => ({ ...m, id: m.key }))}
            foot={{ label: 'Total', newCount: ytd.newCount, wonCount: ytd.wonCount, lostCount: ytd.lostCount, wonValue: money(ytd.wonValue), cumulative: money(ytd.wonValue) }}
          />
        </Block>
      )}

      {S.forecast && (
        <Block>
          <H2 sub={`Open deals by expected close month, ${fc.horizonLabel} (through ${fmtDate(fc.horizonEnd)}). Commit ≥ ${fc.commitT}% · Upside ≥ ${fc.upsideT}% · Pipeline below.`}>Forecast</H2>
          <Kpis items={[
            { label: 'Commit',   value: money(fc.totals.commit),   color: catColor.commit },
            { label: 'Upside',   value: money(fc.totals.upside),   color: catColor.upside },
            { label: 'Pipeline', value: money(fc.totals.pipeline), color: catColor.pipeline },
            { label: 'Weighted forecast', value: money(fc.totals.weighted), sub: `${fc.totals.count} deals in horizon`, color: P.accent },
          ]} />
          <div style={{ display: 'flex', gap: 14, fontFamily: P.font, fontSize: 9, color: P.muted, marginBottom: 6 }}>
            {Object.entries(catLabel).map(([k, l]) => <span key={k}><span style={{ display: 'inline-block', width: 9, height: 9, background: catColor[k], borderRadius: 2, marginRight: 4, verticalAlign: -1 }} />{l}</span>)}
          </div>
          {/* stacked bars */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', height: 120, borderBottom: `1px solid ${P.line}`, padding: '0 2px' }}>
            {fc.months.map(m => {
              const total = m.commit + m.upside + m.pipeline;
              const h = (v) => `${Math.max(0, (v / fc.maxMonth) * 86)}px`;
              return (
                <div key={m.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                  {total > 0 && <div style={{ fontFamily: P.font, fontSize: 8, color: P.muted, marginBottom: 2, whiteSpace: 'nowrap' }}>{money(total)}</div>}
                  <div style={{ width: '100%', display: 'flex', flexDirection: 'column-reverse' }}>
                    <div style={{ height: h(m.commit),   background: catColor.commit }} />
                    <div style={{ height: h(m.upside),   background: catColor.upside }} />
                    <div style={{ height: h(m.pipeline), background: catColor.pipeline, borderRadius: '3px 3px 0 0' }} />
                    {total === 0 && <div style={{ height: 2, background: P.line }} />}
                  </div>
                  <div style={{ fontFamily: P.font, fontSize: 8.5, color: P.muted, marginTop: 4, whiteSpace: 'nowrap' }}>{m.label}</div>
                </div>
              );
            })}
          </div>
          <div style={{ height: 12 }} />
          <Table
            cols={[
              { key: 'label',    label: 'Expected close' },
              { key: 'count',    label: 'Deals',    align: 'right' },
              { key: 'commit',   label: 'Commit',   align: 'right', render: r => money(r.commit) },
              { key: 'upside',   label: 'Upside',   align: 'right', render: r => money(r.upside) },
              { key: 'pipeline', label: 'Pipeline', align: 'right', render: r => money(r.pipeline) },
              { key: 'total',    label: 'Unweighted', align: 'right', render: r => money(r.commit + r.upside + r.pipeline) },
              { key: 'weighted', label: 'Weighted', align: 'right', render: r => money(r.weighted), bold: true },
            ]}
            rows={fc.months.map(m => ({ ...m, id: m.key }))}
            foot={{ label: 'Total', count: fc.totals.count, commit: money(fc.totals.commit), upside: money(fc.totals.upside), pipeline: money(fc.totals.pipeline), total: money(fc.totals.commit + fc.totals.upside + fc.totals.pipeline), weighted: money(fc.totals.weighted) }}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 14 }}>
            <div style={{ background: P.tile, border: `1px solid ${P.line}`, borderRadius: 6, padding: '10px 12px', fontFamily: P.font, fontSize: 10.5 }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: P.muted, marginBottom: 6 }}>{model.fyLabel} projection</div>
              {[
                ['Closed won to date',          money(fc.projection.ytd)],
                ['+ Weighted open, closes in FY', money(fc.projection.weighted - fc.projection.ytd)],
                ['= Projected finish (weighted)', money(fc.projection.weighted)],
                ['Best case (YTD + commit + upside)', money(fc.projection.bestCase)],
              ].map(([k, v], i) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderTop: i === 2 ? `1px solid ${P.navy}` : 'none', fontWeight: i === 2 ? 800 : 400 }}><span>{k}</span><span>{v}</span></div>
              ))}
            </div>
            <div style={{ background: P.tile, border: `1px solid ${P.line}`, borderRadius: 6, padding: '10px 12px', fontFamily: P.font, fontSize: 10.5 }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: P.muted, marginBottom: 6 }}>Outside the horizon</div>
              {[
                ['Past-due close date (still open)', fc.overdue.length,     money(fc.overdue.reduce((s, d) => s + (Number(d.value) || 0), 0))],
                ['No close date set',                fc.unscheduled.length, money(fc.unscheduled.reduce((s, d) => s + (Number(d.value) || 0), 0))],
                ['Closes after horizon',             fc.beyond.length,      money(fc.beyond.reduce((s, d) => s + (Number(d.value) || 0), 0))],
              ].map(([k, n, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', color: n > 0 && k.startsWith('Past') ? '#B91C1C' : P.ink }}><span>{k}</span><span>{n} · {v}</span></div>
              ))}
              <div style={{ fontSize: 9, color: P.muted, marginTop: 6, lineHeight: 1.4 }}>Past-due and undated deals are excluded from the monthly forecast. Update their close dates in the pipeline to bring them in.</div>
            </div>
          </div>
        </Block>
      )}

      {S.funnel && (
        <Block>
          <H2 sub="Current stage snapshot and stage-to-stage conversion across every deal that has entered the funnel.">Pipeline Funnel</H2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
            {fn.rows.map(f => (
              <div key={f.id} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 150px', gap: 10, alignItems: 'center', fontFamily: P.font }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: P.navy }}>{f.label}</div>
                <div style={{ position: 'relative', height: 18, background: '#F3F4F6', borderRadius: 3 }}>
                  <div style={{ position: 'absolute', left: `${(1 - f.reached / fn.maxReached) * 50}%`, width: `${(f.reached / fn.maxReached) * 100}%`, height: '100%', background: f.color, borderRadius: 3, opacity: 0.9 }} />
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9.5, fontWeight: 700, color: '#fff', textShadow: '0 0 3px rgba(0,0,0,0.6)' }}>{f.reached} reached</div>
                </div>
                <div style={{ fontSize: 9.5, color: P.muted, textAlign: 'right' }}>
                  {Number.isFinite(f.conversion) ? `${pct(f.conversion)} → next` : (f.id === 'closed_won' ? 'end of funnel' : '—')}
                </div>
              </div>
            ))}
          </div>
          <Table
            cols={[
              { key: 'label',      label: 'Stage', bold: true },
              { key: 'count',      label: 'Currently at stage', align: 'right' },
              { key: 'value',      label: 'Value at stage',     align: 'right', render: r => money(r.value) },
              { key: 'weighted',   label: 'Weighted',           align: 'right', render: r => money(r.weighted) },
              { key: 'reached',    label: 'Ever reached',       align: 'right' },
              { key: 'conversion', label: 'Conversion to next', align: 'right', render: r => Number.isFinite(r.conversion) ? pct(r.conversion) : '—' },
              { key: 'lostHere',   label: 'Lost from stage',    align: 'right', color: r => r.lostHere > 0 ? '#B91C1C' : P.muted },
            ]}
            rows={fn.rows}
            foot={{ label: 'Totals', count: fn.openCount + fn.wonCount, value: money(fn.rows.reduce((s, r) => s + r.value, 0)), weighted: money(fn.rows.reduce((s, r) => s + r.weighted, 0)), lostHere: fn.lostCount }}
          />
          <div style={{ fontFamily: P.font, fontSize: 9, color: P.muted, marginTop: 8, lineHeight: 1.5 }}>
            "Ever reached" counts every deal whose furthest stage is at or past that stage, including deals since won or lost. Conversion is reached(next) ÷ reached(this). Lost-from-stage uses each lost deal's last recorded stage advance.
          </div>
        </Block>
      )}

      {S.deals && (
        <>
          <Block>
            <H2 sub={`${model.openDeals.length} open deals, ordered by weighted value.`}>Appendix A — Open Deals</H2>
            <Table
              cols={[
                { key: 'title',       label: 'Deal', bold: true },
                { key: 'company',     label: 'Company' },
                { key: 'stage',       label: 'Stage', nowrap: true },
                { key: 'owner',       label: 'Owner', nowrap: true },
                { key: 'closeDate',   label: 'Close', nowrap: true, render: r => fmtDate(r.closeDate) },
                { key: 'category',    label: 'Call', nowrap: true, render: r => catLabel[r.category], color: r => catColor[r.category] },
                { key: 'value',       label: 'Value', align: 'right', render: r => money(r.value) },
                { key: 'probability', label: 'Prob', align: 'right', render: r => `${r.probability}%` },
                { key: 'weighted',    label: 'Weighted', align: 'right', render: r => money(r.weighted), bold: true },
              ]}
              rows={model.openDeals}
              foot={{ title: `${model.openDeals.length} deals`, value: money(model.openDeals.reduce((s, r) => s + r.value, 0)), weighted: money(model.openDeals.reduce((s, r) => s + r.weighted, 0)) }}
              empty="No open deals."
            />
          </Block>
          <Block>
            <H2 sub={`Deals closed won between ${fmtDate(model.fyStart)} and ${fmtDate(model.asOf)}.`}>Appendix B — Closed Won YTD</H2>
            <Table
              cols={[
                { key: 'title',     label: 'Deal', bold: true },
                { key: 'company',   label: 'Company' },
                { key: 'owner',     label: 'Owner', nowrap: true },
                { key: 'closeDate', label: 'Closed', nowrap: true, render: r => fmtDate(r.closeDate) },
                { key: 'value',     label: 'Value', align: 'right', render: r => money(r.value), bold: true },
              ]}
              rows={model.wonDealsYtd}
              foot={{ title: `${model.wonDealsYtd.length} deals`, value: money(ytd.wonValue) }}
              empty="Nothing closed won yet this fiscal year."
            />
          </Block>
        </>
      )}

      <Block style={{ marginBottom: 0 }}>
        <div style={{ borderTop: `1px solid ${P.line}`, paddingTop: 8, fontFamily: P.font, fontSize: 8.5, color: P.muted, display: 'flex', justifyContent: 'space-between' }}>
          <span>{orgName} · {settings.title || 'Sales Performance Report'} · Confidential</span>
          <span>Generated {new Date().toLocaleString('en-US')}</span>
        </div>
      </Block>
    </div>
  );
}

// ─── PDF export ───────────────────────────────────────────────────
async function exportReportPdf({ paperEl, filename, footerText, onStage }) {
  const { default: html2canvas } = await import('html2canvas');
  const { jsPDF } = await import('jspdf');

  // Clone off-screen so scroll offsets / transforms in the preview
  // container can't skew the capture.
  const wrapper = document.createElement('div');
  wrapper.style.cssText = 'position:fixed;left:-20000px;top:0;width:816px;background:#fff;z-index:-1;';
  const clone = paperEl.cloneNode(true);
  clone.style.boxShadow = 'none';
  wrapper.appendChild(clone);
  document.body.appendChild(wrapper);

  try {
    const blocks = Array.from(clone.querySelectorAll('[data-report-block]'));
    const pdf = new jsPDF({ unit: 'in', format: 'letter', orientation: 'portrait' });
    const pageW = 8.5, pageH = 11, mx = 0.5, myTop = 0.5, myBot = 0.6;
    const imgW = pageW - mx * 2;
    const contentH = pageH - myTop - myBot;
    const gap = 0.12;
    let y = myTop;

    const toJpeg = (canvas) => {
      const c = document.createElement('canvas');
      c.width = canvas.width; c.height = canvas.height;
      const ctx = c.getContext('2d');
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, c.width, c.height);
      ctx.drawImage(canvas, 0, 0);
      return c.toDataURL('image/jpeg', 0.94);
    };

    for (let i = 0; i < blocks.length; i++) {
      onStage?.(`Rendering section ${i + 1} of ${blocks.length}…`);
      const canvas = await html2canvas(blocks[i], { scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false });
      if (!canvas.width || !canvas.height) continue;
      const imgH = canvas.height * (imgW / canvas.width);

      if (imgH <= contentH) {
        if (y > myTop && y + imgH > myTop + contentH) { pdf.addPage(); y = myTop; }
        pdf.addImage(toJpeg(canvas), 'JPEG', mx, y, imgW, imgH);
        y += imgH + gap;
        continue;
      }
      // Taller than a page: start on a fresh page and slice.
      if (y > myTop) { pdf.addPage(); y = myTop; }
      const pxPerIn = canvas.width / imgW;
      const sliceHPx = Math.floor(contentH * pxPerIn);
      let src = 0;
      while (src < canvas.height) {
        const h = Math.min(sliceHPx, canvas.height - src);
        const slice = document.createElement('canvas');
        slice.width = canvas.width; slice.height = h;
        const sctx = slice.getContext('2d');
        sctx.fillStyle = '#ffffff'; sctx.fillRect(0, 0, slice.width, slice.height);
        sctx.drawImage(canvas, 0, src, canvas.width, h, 0, 0, canvas.width, h);
        if (src > 0) { pdf.addPage(); y = myTop; }
        pdf.addImage(slice.toDataURL('image/jpeg', 0.94), 'JPEG', mx, y, imgW, h / pxPerIn);
        y = myTop + h / pxPerIn + gap;
        src += h;
      }
    }

    const n = pdf.getNumberOfPages();
    for (let p = 1; p <= n; p++) {
      pdf.setPage(p);
      pdf.setFontSize(8); pdf.setTextColor(120);
      pdf.text(footerText, mx, pageH - 0.3);
      pdf.text(`Page ${p} of ${n}`, pageW - mx, pageH - 0.3, { align: 'right' });
    }
    pdf.save(filename);
    return n;
  } finally {
    document.body.removeChild(wrapper);
  }
}

// ─── settings panel (dark, matches the app) ──────────────────────
const F = {
  label: { fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: 5, display: 'block' },
  input: { width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, padding: '8px 10px', color: '#fff', fontFamily: "'Montserrat',sans-serif", fontSize: 12, outline: 'none' },
  group: { marginBottom: 14 },
  check: { display: 'flex', alignItems: 'center', gap: 8, fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.75)', padding: '4px 0', cursor: 'pointer' },
};

export default function CRMReports({ deals = [], contracts = [], stages = [], currentUser, orgSettings, users = [], showToast }) {
  const orgId = currentUser?.orgId || 'default';
  const KEY = `nuggets_crm_report_${orgId}`;
  const [settings, setSettings] = useState(() => {
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem(KEY)); } catch {}
    const base = { ...DEFAULT_REPORT_SETTINGS, ...(saved || {}), sections: { ...DEFAULT_REPORT_SETTINGS.sections, ...(saved?.sections || {}) } };
    // "As of" always defaults to today unless the user pinned one this session.
    if (!saved?.asOfPinned) base.asOf = todayIso();
    if (!base.preparedBy) base.preparedBy = currentUser?.name || '';
    return base;
  });
  useEffect(() => { try { localStorage.setItem(KEY, JSON.stringify(settings)); } catch {} }, [KEY, settings]);

  const [exporting, setExporting] = useState(false);
  const [stage, setStage]         = useState('');
  const paperRef = useRef(null);

  const set = (k, v) => setSettings(s => ({ ...s, [k]: v }));
  const setSection = (k, v) => setSettings(s => ({ ...s, sections: { ...s.sections, [k]: v } }));

  const model = useMemo(() => buildReportModel({ deals, contracts, stages, settings, users }), [deals, contracts, stages, settings, users]);
  const orgName = orgSettings?.org_name || '40Geo';
  const owners = useMemo(() => {
    const ids = new Set(deals.map(d => d.ownerUserId).filter(Boolean));
    return users.filter(u => ids.has(u.id));
  }, [deals, users]);

  const onExport = async () => {
    if (exporting || !paperRef.current) return;
    setExporting(true);
    try {
      const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 50);
      const filename = `${slug(orgName)}_${slug(settings.title)}_${model.fyLabel}_${settings.asOf}.pdf`;
      const pages = await exportReportPdf({
        paperEl: paperRef.current, filename,
        footerText: `${orgName} · ${settings.title} · ${model.fyLabel} · as of ${fmtDate(model.asOf)} · Confidential`,
        onStage: setStage,
      });
      showToast?.(`✓ Report exported — ${pages} page${pages === 1 ? '' : 's'}`);
    } catch (err) {
      console.error('[crm-report] export failed', err);
      showToast?.(`❌ Export failed: ${err.message || err}`);
    } finally {
      setExporting(false); setStage('');
    }
  };

  const reset = () => setSettings({ ...DEFAULT_REPORT_SETTINGS, asOf: todayIso(), preparedBy: currentUser?.name || '' });

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '300px minmax(0, 1fr)', gap: 20, alignItems: 'start' }}>
      {/* Settings */}
      <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '18px 18px 10px', position: 'sticky', top: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)' }}>Report settings</div>
          <button onClick={reset} title="Reset to defaults" style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontFamily: "'Montserrat',sans-serif" }}><RotateCcw size={12} /> Reset</button>
        </div>

        <div style={F.group}><label style={F.label}>Report title</label>
          <input style={F.input} value={settings.title} onChange={e => set('title', e.target.value)} /></div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={F.group}><label style={F.label}>Fiscal year starts</label>
            <select style={F.input} value={settings.fiscalYearStartMonth} onChange={e => set('fiscalYearStartMonth', Number(e.target.value))}>
              {MONTHS.map((m, i) => <option key={m} value={i} style={{ color: '#000' }}>{m}</option>)}
            </select></div>
          <div style={F.group}><label style={F.label}>As of</label>
            <input type="date" style={F.input} value={settings.asOf} onChange={e => setSettings(s => ({ ...s, asOf: e.target.value || todayIso(), asOfPinned: !!e.target.value }))} /></div>
        </div>

        <div style={F.group}><label style={F.label}>Forecast horizon</label>
          <select style={F.input} value={settings.horizon} onChange={e => set('horizon', e.target.value)}>
            <option value="fy" style={{ color: '#000' }}>Rest of fiscal year</option>
            <option value="3"  style={{ color: '#000' }}>Next 3 months</option>
            <option value="6"  style={{ color: '#000' }}>Next 6 months</option>
            <option value="12" style={{ color: '#000' }}>Next 12 months</option>
          </select></div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={F.group}><label style={F.label}>Commit ≥ %</label>
            <input type="number" min={1} max={100} style={F.input} value={settings.commitThreshold} onChange={e => set('commitThreshold', Math.min(100, Math.max(1, Number(e.target.value) || 0)))} /></div>
          <div style={F.group}><label style={F.label}>Upside ≥ %</label>
            <input type="number" min={0} max={100} style={F.input} value={settings.upsideThreshold} onChange={e => set('upsideThreshold', Math.min(100, Math.max(0, Number(e.target.value) || 0)))} /></div>
        </div>

        <div style={F.group}><label style={F.label}>Deal owner</label>
          <select style={F.input} value={settings.ownerUserId} onChange={e => set('ownerUserId', e.target.value)}>
            <option value="" style={{ color: '#000' }}>All owners</option>
            {owners.map(u => <option key={u.id} value={u.id} style={{ color: '#000' }}>{u.name}</option>)}
          </select></div>

        <div style={F.group}><label style={F.label}>Prepared by</label>
          <input style={F.input} value={settings.preparedBy} onChange={e => set('preparedBy', e.target.value)} /></div>

        <div style={F.group}><label style={F.label}>Executive commentary</label>
          <textarea rows={4} style={{ ...F.input, resize: 'vertical', lineHeight: 1.5 }} placeholder="Optional narrative shown under the report header." value={settings.commentary} onChange={e => set('commentary', e.target.value)} /></div>

        <div style={F.group}><label style={F.label}>Sections</label>
          {[['ytd', 'Year-to-date performance'], ['forecast', 'Forecast'], ['funnel', 'Pipeline funnel'], ['deals', 'Deal appendices']].map(([k, l]) => (
            <label key={k} style={F.check}><input type="checkbox" checked={!!settings.sections?.[k]} onChange={e => setSection(k, e.target.checked)} /> {l}</label>
          ))}
        </div>

        <button onClick={onExport} disabled={exporting}
          style={{ width: '100%', marginTop: 4, marginBottom: 8, background: exporting ? 'rgba(232,119,34,0.5)' : '#E87722', color: '#fff', border: 'none', borderRadius: 6, padding: '11px 14px', fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: exporting ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <FileDown size={15} /> {exporting ? (stage || 'Exporting…') : 'Export PDF'}
        </button>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.3)', lineHeight: 1.5, marginBottom: 6 }}>
          Letter, portrait. The preview on the right is exactly what prints.
        </div>
      </div>

      {/* Preview */}
      <div style={{ background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: 24, overflowX: 'auto' }}>
        <ReportPaper innerRef={paperRef} model={model} settings={settings} orgName={orgName} logoUrl={orgSettings?.logo_url || null} />
      </div>
    </div>
  );
}
